import { MUSCLE_GROUP_IDS, searchExercises, suggestExercises, type Exercise, type ExerciseHistory, type MuscleGroup } from '@squats/shared';
import { useMemo, useState } from 'react';
import { Link } from 'wouter';
import { formatMonthDay } from '../dates.ts';

/** Pick an exercise: suggestions for the muscle groups (your usual ones first), or search the whole library. */
export function ExercisePicker({
  exercises,
  groups,
  history,
  today,
  exclude,
  onPick,
  suggestionsTitle,
}: {
  exercises: Exercise[];
  groups: readonly MuscleGroup[];
  history: ExerciseHistory[];
  today: string;
  exclude: string[];
  onPick: (id: string) => void;
  suggestionsTitle?: string;
}) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  // No split: suggest what you do most, across everything.
  const suggestions = useMemo(() => {
    const all = suggestExercises(exercises, groups.length ? groups : MUSCLE_GROUP_IDS, history, today, exclude);
    return (groups.length ? all : all.filter((s) => s.history)).slice(0, 8);
  }, [exercises, groups, history, today, exclude]);
  const matches = q ? searchExercises(exercises.filter((e) => !exclude.includes(e.id)), q).slice(0, 30) : [];

  const row = (e: Exercise, h?: ExerciseHistory) => (
    <li key={e.id}>
      <button type="button" className="list-row pick-row" onClick={() => onPick(e.id)}>
        <span className="grow stack-sm">
          <span className="row-title">{e.name}</span>
          {h && (
            <span className="muted small">
              Last {formatMonthDay(h.lastDate)}
              {h.recentCount > 1 && ` · ${h.recentCount}× in 90 days`}
            </span>
          )}
        </span>
        <span aria-hidden="true">+</span>
      </button>
    </li>
  );

  return (
    <div className="stack">
      <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search all exercises" aria-label="Search all exercises" />
      {q ? (
        matches.length ? (
          <ul className="list">
            {matches.map((e) =>
              row(
                e,
                history.find((h) => h.exerciseId === e.id),
              ),
            )}
          </ul>
        ) : (
          <p className="muted">
            No match. You can add new exercises on the <Link href="/exercises">Exercises</Link> page.
          </p>
        )
      ) : (
        <>
          <h3>{suggestionsTitle ?? (groups.length ? 'Suggested for today' : 'Your usual exercises')}</h3>
          {suggestions.length ? <ul className="list">{suggestions.map((s) => row(s.exercise, s.history))}</ul> : <p className="muted">Search to find an exercise.</p>}
        </>
      )}
    </div>
  );
}
